import { Badge } from "@/components/ui/badge";

interface TeamSplit {
  role: string;
  splitPercent: number;
  commission: number;
}

interface CommissionBreakdown {
  amount: number;
  rate: number;
  commission: number;
  tier?: string;
}

interface MechaCommissionData {
  calculationType: "single" | "team";
  saleAmount: number;
  commissionRate: number;
  totalCommission: number;
  breakdown: CommissionBreakdown;
  teamSplits?: TeamSplit[];
}

function formatCurrency(n: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

interface MechaCommissionBlockProps {
  data: MechaCommissionData;
}

export function MechaCommissionBlock({ data }: MechaCommissionBlockProps) {
  const { saleAmount, commissionRate, totalCommission, breakdown, teamSplits } = data;

  return (
    <div className="rounded-lg border border-border/50 bg-card/50 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/30 bg-muted/30">
        <h3 className="text-sm font-medium text-foreground">Commission Calculation</h3>
        {breakdown.tier && (
          <Badge variant="outline" className="text-xs">
            {breakdown.tier} tier
          </Badge>
        )}
      </div>

      <div className="p-4 space-y-4">
        {/* Summary */}
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Sale Amount</p>
            <p className="text-lg font-semibold text-foreground">{formatCurrency(saleAmount)}</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Rate</p>
            <p className="text-lg font-semibold text-foreground">{(commissionRate * 100).toFixed(1)}%</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Commission</p>
            <p className="text-lg font-semibold text-green-400">{formatCurrency(totalCommission)}</p>
          </div>
        </div>

        {/* Team splits */}
        {teamSplits && teamSplits.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Team Split</p>
            <div className="space-y-1.5">
              {teamSplits.map((split) => (
                <div
                  key={split.role}
                  className="flex items-center justify-between px-3 py-2 rounded-md bg-muted/20"
                >
                  <span className="text-sm text-foreground">{split.role}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">{split.splitPercent}%</span>
                    <span className="text-sm font-medium text-foreground">{formatCurrency(split.commission)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
